import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { PARAM_DEFS } from 'common/constants/physicalParams';

const SIZE = 150;
const STROKE = 16;
const RADIUS = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * RADIUS;
// Paleta diversa pero armónica (un color por parámetro). Tonos algo desaturados → se ven elegantes
// y con buen contraste tanto en dark como en light.
const PALETTE = ['#F9BD4E', '#E07A5F', '#6C8EBF', '#81B29A', '#B084CC', '#4FB6A8', '#E9C46A'];

/**
 * Donut SVG (sin dependencias) con las medidas de la ÚLTIMA entrada física. Porciones relativas;
 * al pasar el mouse o hacer clic en una porción se muestra su valor al centro (y resalta la leyenda).
 */
function ProgressMeasurementsDonut({ entry }) {
  const theme = useTheme();
  const [active, setActive] = useState(null);

  const items = useMemo(
    () => PARAM_DEFS
      .map((p, i) => ({ ...p, value: entry?.[p.key] != null ? Number(entry[p.key]) : null, color: PALETTE[i % PALETTE.length] }))
      .filter((p) => p.value != null && p.value > 0),
    [entry]
  );

  const total = items.reduce((s, p) => s + p.value, 0);

  const segments = useMemo(() => {
    if (total === 0) return [];
    let offset = 0;
    return items.map((p) => {
      const length = (p.value / total) * CIRC;
      const seg = { ...p, dasharray: `${length} ${CIRC - length}`, dashoffset: -offset };
      offset += length;
      return seg;
    });
  }, [items, total]);

  const dateLabel = entry?.taken_at || (entry?.created_at || '').slice(0, 10);
  const activeItem = items.find((p) => p.key === active);

  if (items.length === 0) return null;

  return (
    <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 2.5 }, height: '100%' }}>
      <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Resumen última entrada</Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
        <Box sx={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
          <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
            <circle cx={SIZE / 2} cy={SIZE / 2} r={RADIUS} fill="none" stroke={theme.palette.hairline.strong} strokeWidth={STROKE} />
            {segments.map((s) => (
              <circle
                key={s.key}
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={s.color}
                strokeWidth={STROKE}
                strokeDasharray={s.dasharray}
                strokeDashoffset={s.dashoffset}
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
                style={{ cursor: 'pointer', opacity: active && active !== s.key ? 0.4 : 1, transition: 'opacity 0.2s' }}
                onMouseEnter={() => setActive(s.key)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((cur) => (cur === s.key ? null : s.key))}
              />
            ))}
          </svg>
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 1 }}>
            {activeItem ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1, color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>
                  {activeItem.value}{activeItem.unit ? ` ${activeItem.unit}` : ''}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{activeItem.label}</Typography>
              </>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Última</Typography>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{dateLabel}</Typography>
              </>
            )}
          </Box>
        </Box>
        <Stack spacing={0.75} sx={{ flexGrow: 1, width: '100%' }}>
          {items.map((p) => (
            <Stack
              key={p.key}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onMouseEnter={() => setActive(p.key)}
              onMouseLeave={() => setActive(null)}
              sx={{ cursor: 'pointer', opacity: active && active !== p.key ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: p.color }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.label}</Typography>
              </Stack>
              <Typography variant="caption" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {p.value}{p.unit ? ` ${p.unit}` : ''}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Box>
  );
}

ProgressMeasurementsDonut.propTypes = { entry: PropTypes.object.isRequired };

export default ProgressMeasurementsDonut;
